'use client'

import { useState, useEffect } from 'react'
import { User } from '@supabase/supabase-js'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import { UserDropdown } from './user-dropdown'
import { usePathname, useRouter } from 'next/navigation'
import { appendRedirectParam } from '../../lib/safe-redirect'
import { SigninWithGoogleButton } from './signin-with-google-button'
import { createClient } from "@/lib/supabase/client"

interface AuthButtonsProps {
  signInVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  signUpVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  redirectTo?: string
  showSignIn?: boolean
  showSignUp?: boolean
  showGoogleSignIn?: boolean
  className?: string
}

export function AuthButtons({ 
  signInVariant = 'ghost', 
  signUpVariant = 'default',
  size = 'sm',
  redirectTo,
  showSignIn = true,
  showSignUp = true,
  showGoogleSignIn = true,
  className = ''
}: AuthButtonsProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const supabase = createClient()
  const pathname = usePathname()
  
  // Use the current path as the redirect URL if none is provided
  const redirect = redirectTo || pathname

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        setUser(user)
      } catch (error) {
        console.error('Error fetching user:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchUser()

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null)
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [supabase])

  if (loading) {
    return <Button variant="ghost" size={size} disabled>Loading...</Button>
  }

  if (!user) {
    // Create login and signup URLs with the redirect parameter
    const loginUrl = appendRedirectParam({
      baseUrl: '/auth/login',
      redirectUrl: redirect
    })
    
    const signupUrl = appendRedirectParam({
      baseUrl: '/auth/signup',
      redirectUrl: redirect
    })
    
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {showGoogleSignIn && (
          <SigninWithGoogleButton 
            id="header-google-signin" 
            size="medium" 
            redirectTo={redirect}
          />
        )}
        {showSignUp && (
          <Link href={signupUrl}>
            <Button variant={signUpVariant} size={size}>Sign Up</Button>
          </Link>
        )}
        {showSignIn && (
          <Link href={loginUrl}>
            <Button variant={signInVariant} size={size}>Sign In</Button>
          </Link>
        )}
      </div>
    )
  }

  return <UserDropdown className={`flex items-center ${className}`} />
}

export function SignInButton() {
  const router = useRouter()
  const supabase = createClient()

  return (
    <Button
      onClick={async () => {
        await supabase.auth.signInWithOAuth({
          provider: 'github',
          options: {
            redirectTo: `${window.location.origin}/auth/callback`
          }
        })
      }}
    >
      Sign In
    </Button>
  )
}

export function SignOutButton() {
  const router = useRouter()
  const supabase = createClient()

  return (
    <Button
      variant="outline"
      onClick={async () => {
        await supabase.auth.signOut()
        router.refresh()
      }}
    >
      Sign Out
    </Button>
  )
} 